// The stop-a-task write flow (#1906) — the state half of <StopTaskModal/>, split
// from its surfaces exactly as `createMachineRemove` is: a mutation with a confirm
// step, a re-entrancy guard and a failure notice is state, not rendering.
// Per-surface, never a global store — each route owns one, and it unit-tests
// without a shell.
//
// Three surfaces share this (the queue board, a run's view, an epic's slices), so
// the confirm copy has to name the work rather than an id. `StopTarget` carries
// the title and the repo for that reason and for no other.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { errorReason } from './api';
import { cancelTask, type TaskCancelResult } from './fleet';

/**
 * The work a stop is about to end, in the words a human recognises.
 *
 * An id is not enough: "Stop tsk_9f3c…?" asks someone to verify a hash before a
 * destructive action, which is how a wrong row gets stopped. The title and the
 * repo are what the reader was already looking at.
 */
export type StopTarget = {
  readonly id: string;
  readonly title: string;
  readonly repo_full_name: string;
};

export type TaskStopController = {
  /** The task awaiting confirmation, or null. Feed to <StopTaskModal task=…/>. */
  readonly target: () => StopTarget | null;
  /** Failure copy for the surface's <Toast/>, or null. */
  readonly error: () => string | null;
  /** Open the confirm for a task, clearing any previous failure. */
  readonly ask: (task: StopTarget) => void;
  /** Dismiss the confirm without stopping. */
  readonly close: () => void;
  /** Clear the failure notice — wire to the <Toast/>'s `onDismiss`. */
  readonly clearError: () => void;
  /** Stop the task under confirmation. A second call while one is in flight is dropped. */
  readonly confirm: () => void;
};

/**
 * Stop state for one surface. `onStopped` fires once the API has accepted — the
 * caller does NOT need to refetch on the live surfaces (the `tasks` stream patches
 * the row in place), but it is how a surface without a stream re-reads.
 *
 * A failure leaves the dialog OPEN with `error()` set: a destructive action must
 * never silently no-op. The message is the server's own where it sent one — a
 * `pr_review` task answers 409 with the reason it cannot be stopped, and relaying
 * that is the difference between "it refused, here is why" and a dead button.
 */
export function createTaskStop(input: {
  readonly token: string | null;
  readonly onStopped: (result: { readonly alreadyTerminal: boolean }) => void;
  /**
   * The cancel door, a seam (#6753): the test injects its own rather than replacing the
   * `./fleet` module, which leaked the stand-in into every later test file in the process.
   */
  readonly cancel?: (taskId: string, token: string | null) => Promise<TaskCancelResult>;
}): TaskStopController {
  const cancel = input.cancel ?? cancelTask;
  const [target, setTarget] = createSignal<StopTarget | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  // Busy guard instead of a disabled action button (ConfirmDialog has none) —
  // the same shape `createMachineRemove` uses.
  let busy = false;

  const confirm = (): void => {
    const row = target();
    if (row === null || busy) return;
    busy = true;
    setError(null);
    void cancel(row.id, input.token)
      .then((result) => {
        // Close only the confirm THIS call opened. Nothing stops the reader
        // dismissing it and asking about a different task while the stop is in
        // flight (`busy` gates a second confirm, not `close`/`ask`), and a blind
        // `setTarget(null)` would dismiss that newer dialog out from under them —
        // reading as a phantom cancel of work they never stopped.
        if (target()?.id === row.id) setTarget(null);
        input.onStopped({ alreadyTerminal: result.kind === 'already_terminal' });
      })
      // The server's sentence when it sent one: the 409 a `pr_review` task earns
      // NAMES why it kept running, and replacing that with our generic copy is how
      // a refusal becomes indistinguishable from a broken button.
      .catch((err: unknown) => setError(errorReason(err, t('fleet.stop.error'))))
      .finally(() => {
        busy = false;
      });
  };

  return {
    target,
    error,
    ask: (task) => {
      setError(null);
      setTarget(task);
    },
    close: () => setTarget(null),
    clearError: () => setError(null),
    confirm,
  };
}
